import type { Card } from '@blueprint/domain'
import { useDeferredValue, useMemo } from 'react'
import { useBoardStore } from './board-store.js'
import { filtersAreActive, useUiStore, type Filters } from './ui-store.js'

export interface FilterIndex {
  active: boolean
  /** Ids of cards matching every populated filter, or null when no filter is set. */
  matchSet: ReadonlySet<string> | null
  matching: number
  total: number
  facets: { owners: string[]; tags: string[]; releases: string[] }
}

function matches(card: Card, filters: Filters, needle: string): boolean {
  if (needle.length > 0) {
    const haystack = `${card.title}\n${card.description}`.toLowerCase()
    if (!haystack.includes(needle)) return false
  }
  if (filters.status !== '' && card.status !== filters.status) return false
  if (filters.priority !== '' && card.priority !== filters.priority) return false
  if (filters.moscow !== '' && card.moscow !== filters.moscow) return false
  const owner = filters.owner.trim().toLowerCase()
  if (owner.length > 0 && !card.owner.toLowerCase().includes(owner)) return false
  const release = filters.release.trim().toLowerCase()
  if (release.length > 0 && !card.release.toLowerCase().includes(release)) return false
  const tag = filters.tag.trim().toLowerCase()
  if (tag.length > 0 && !card.tags.some((value) => value.toLowerCase() === tag)) return false
  return true
}

function sortedUnique(values: Iterable<string>): string[] {
  return [...new Set(values)].filter((value) => value.length > 0).sort((a, b) => a.localeCompare(b))
}

const NO_FACETS = { owners: [] as string[], tags: [] as string[], releases: [] as string[] }

/**
 * Scans the loaded board's cards after a deferred update, so typing in the search box
 * never blocks input. Populated filters combine with AND.
 */
export function useFilterIndex(): FilterIndex {
  const cards = useBoardStore((state) => state.entities?.cards)
  const liveFilters = useUiStore((state) => state.filters)
  const filters = useDeferredValue(liveFilters)

  return useMemo(() => {
    if (!cards) return { active: false, matchSet: null, matching: 0, total: 0, facets: NO_FACETS }
    const all = Object.values(cards)
    const facets = {
      owners: sortedUnique(all.map((card) => card.owner)),
      tags: sortedUnique(all.flatMap((card) => card.tags)),
      releases: sortedUnique(all.map((card) => card.release)),
    }
    if (!filtersAreActive(filters)) {
      return { active: false, matchSet: null, matching: all.length, total: all.length, facets }
    }
    const needle = filters.search.trim().toLowerCase()
    const matchSet = new Set<string>()
    for (const card of all) if (matches(card, filters, needle)) matchSet.add(card.id)
    return { active: true, matchSet, matching: matchSet.size, total: all.length, facets }
  }, [cards, filters])
}
